<template>
  <v-layout>
    <v-dialog v-if="planningLoad"
              v-model="showDispatchToNetworkDialog"
              lazy
              persistent
              @input="v => v"
              max-width="500">

      <v-card class="pa-4" >
        <span class="title mb-3">Dispatch to Carrier Network</span>

        <v-list two-line>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title v-if="planningLoadCustomerName">
                Customer: {{ planningLoadCustomerName }}
              </v-list-tile-title>
              <v-list-tile-sub-title class="font-italic">
                Bills ${{billableAmount}} for {{ distanceLabel(loadedMiles) }}, ${{loadedDistanceBilledRate}}
                per loaded {{ siteDistanceMeasureUnitLabel }}
              </v-list-tile-sub-title>
              <v-list-tile-sub-title class="font-italic">
                Based on Google Maps
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile v-if="planningLoadCustomerName">
            <v-list-tile-avatar>
              <v-checkbox
                :prepend-icon="includeCustomerIcon"
                v-model="includeCustomerOnDispatch"></v-checkbox>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ includeCustomerOnDispatchLabel }}
              </v-list-tile-title>
              <v-list-tile-sub-title class="font-italic">
                customer is hidden from dispatched subhauler
              </v-list-tile-sub-title>
              <v-list-tile-sub-title class="font-italic">
                unless checked
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider/>
          <v-list-tile class="pt-3">
            <v-list-tile-action>
              <v-select v-model="visibleCount"
                        label="Visible load count"
                        :items="_.range(1, 1+planningLoad.loadGroup.loadCount)"
              />
            </v-list-tile-action>
          </v-list-tile>
          <v-list-tile>
            <v-list-tile-content>
              <v-float-field label="Payable Amount ($)"
                             v-model="dispatchPayableAmount"/>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-sub-title class="font-italic">
                ${{ billableAmount - dispatchPayableAmount }} spread per load
              </v-list-tile-sub-title>
              <v-list-tile-sub-title class="font-italic">
                Pays {{ distanceLabel(loadedMiles)}}, ${{loadedDistancePaidRate}} per loaded {{ siteDistanceMeasureUnitLabel }}
              </v-list-tile-sub-title>
              <v-list-tile-sub-title class="font-italic">
                Based on Google Maps
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider/>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-checkbox
                :prepend-icon="bookLoadAssignments ? 'schedule' : 'access_time_filled'"
                v-model="bookLoadAssignments"/>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title>
                Book assignment immediately
              </v-list-tile-title>
              <v-list-tile-sub-title class="font-italic">
                load not booked until company accepts dispatch offer
              </v-list-tile-sub-title>
              <v-list-tile-sub-title class="font-italic">
                unless checked
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile>
              <v-select
                v-model="selectedMembers"
                :items="cdnMembers"
                item-text="companyName"
                item-value="companyId"
                :label="`Select Preferred Carrier${bookLoadAssignments ? '' : 's'}`"
                menu-props="{ closeOnContentClick: true, closeOnClick: true }"
                :multiple="!bookLoadAssignments">
                <v-list-tile
                  slot="prepend-item"
                  v-if="!bookLoadAssignments"
                  ripple
                  @click="selectAllCDNMembers">
                  <v-list-tile-action>
                    <v-icon>{{ icon }}</v-icon>
                  </v-list-tile-action>
                  <v-list-tile-title>All</v-list-tile-title>
                </v-list-tile>
                <v-divider slot="prepend-item" v-if="!bookLoadAssignments" class="mt-2"></v-divider>
                <v-divider slot="append-item" v-if="!bookLoadAssignments" class="mb-2"></v-divider>
                <template
                  slot="selection"
                  slot-scope="{ item, index }">
                  <span v-if="index === 0">
                    {{ item.companyName }}
                  </span>
                  <span
                    v-if="index === 1"
                    class="grey--text caption ml-2">
                    (+{{ selectedMembers.length - 1 }} others)
                  </span>
                </template>
              </v-select>

          </v-list-tile>
        </v-list>

        <v-spacer class="mt-3"/>

        <v-card-actions>
          <v-spacer />
          <v-btn color="accent"
                 :disabled="!dispatchPayableAmount"
                 :loading="loadingBtn"
                 @click="dispatchLoad">
            Dispatch
          </v-btn>
          <v-btn color="accent" flat
                 @click="resetDispatchToNetworkDialog()">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="planningLoad && isLoadboardEnabled"
              :full-width="!isMobile"
              max-width="500"
              v-model="showPostToLoadBoardDialog" lazy>
      <v-card class="pa-4">
        <v-layout>
          <v-flex>
            <v-text-field v-model="visibleCount"
                          :step="1"
                          :min="1"
                          :max="planningLoad.loadGroup.loadCount"
                          type="number"
                          label="Visible load count">
            </v-text-field>
          </v-flex>
        </v-layout>

        <template v-if="_.get(planningLoad, 'loadGroup.details.customerCompany')">
          <span class="title mb-3">
            Would you like to include the customer on the Load Details page and {{ $t('terms.bill-of-lading') }}?
          </span>
          <br/>
          <i>Customer: {{ planningLoadCustomerName }}</i>

          <v-card-actions>
            <v-spacer />
            <v-btn color="accent" @click="showLiveLoadBoardInfoDialog = true" fab small flat>
              <v-icon>help_outline</v-icon>
            </v-btn>
            <v-btn color="accent"
                   :loading="loadingBtn"
                   :disabled="errors.any('modal')"
                   @click="postToLoadBoard(true)">
              <v-icon class="pr-2">person_add</v-icon>
              Yes
            </v-btn>
            <v-btn color="accent"
                   :loading="loadingBtn"
                   :disabled="errors.any('modal')"
                   @click="postToLoadBoard(false)">
              <v-icon class="pr-2">person_add_disabled</v-icon>
              No
            </v-btn>
            <v-btn color="accent" flat
                   @click="resetPostToLoadBoardDialog()">
              Cancel
            </v-btn>
          </v-card-actions>
        </template>
        <v-card-actions v-else>
          <v-spacer />
          <v-btn color="accent"
                 :loading="loadingBtn"
                 @click="postToLoadBoard(false)">
            <v-icon class="pr-2">gavel</v-icon>
            Post to Live Load Board
          </v-btn>
          <v-btn color="accent" flat
                 @click="resetPostToLoadBoardDialog()">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <assign-truck-dialog  v-model="showAssignTrucksDialog"
                          @refresh="updateLoad($event)"
                          :company="company"
                          :load="planningLoad">
    </assign-truck-dialog>

    <vendor-dispatch-dialog v-model="showAssignVendorDialog"
                            @refresh="updateLoad($event)"
                            :company="company"
                            :load="planningLoad">
    </vendor-dispatch-dialog>

    <assign-truck-dialog v-model="showOutsideAssignmentDialog"
                         @refresh="updateLoad($event)"
                         :company="company"
                         :load="planningLoad"
                         :outside-trucks="true">
    </assign-truck-dialog>

    <v-dialog v-model="showLiveLoadBoardInfoDialog"
              v-if="isLoadboardEnabled"
              :full-width="!isMobile"
              max-width="500">
      <v-card class="pa-4">
        <span class="title mb-3">Live Load Board Post</span>
        <br />
        Post a listing to the live Load Board and start getting bids from available haulers.
        <br/>
        <br/>
        If you select a bid from the live Load Board, the resulting booked load optionally will
        include the customer.  If it is your load, you are the customer and you will be on it.
        <br/>
        <br/>
        <span class="title mb-3">Dispatched Loads</span>
        <br/>
        If this load is for a customer, you can optionally remove the customer's visibility when you
        post to the Load Board.
        <br/>
        <br/>
        <v-icon color="primary">person_add</v-icon>
        <strong>Include Customer.</strong> When a customer is included,
        they will have access to the Load Details page and will be on the {{ $t('terms.bill-of-lading') }}. They will
        receive updates and notifications as well.
        <br/>
        <br/>
        <v-icon color="primary">person_add_disabled</v-icon>
        <strong>Customer not Included.</strong> If a customer is not included on the load, they will
        not be able to follow progress on the Load Details page, they will not receive updates or
        notifications, and they will not be on the {{ $t('terms.bill-of-lading') }}.
        <br/>
        <br/>
        If it is posted, you can remove the posting, which means you will no longer get new bids.
        You can always post it again if you need to.

        <v-card-actions>
          <v-spacer />
          <v-btn color="accent" flat
                 @click="showLiveLoadBoardInfoDialog = false">
            Got It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCarrierNetworkInfoDialog"
              :full-width="!isMobile"
              max-width="500">
      <v-card class="pa-4">
        <span class="title mb-3">Preferred Carrier Assignment</span>
        <br />
        Send a request to all or some members of your Carrier Network at a price you set, optionally
        including the customer on the load and {{ $t('terms.bill-of-lading') }}.
        <v-card-actions>
          <v-spacer />
          <v-btn color="accent" flat
                 @click="showCarrierNetworkInfoDialog = false">
            Got It
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-repeat-load-group-dialog v-if="planningLoad"
                                v-model="showCopyLoadDialog"
                                :loadGroupId="loadGroup.humanId"
                                :sponsor="loadGroupSponsorName(loadGroup)"
                                :origin="loadGroup.details | originName"
                                :destination="loadGroup.details | destinationName"
                                :count="loadGroup.loadCount || 1"
                                :isMobile="isMobile"
                                :loading="loadingBtn"
                                :company="company"
                                :load-group="loadGroup"
                                @cancel="closeCopyLoadDialog"
                                @newLoad="closeCopyLoadDialog(true)"
    />

    <v-load-group-details v-if="planningLoad"
                          planning
                          @reload="$emit('reload')"
                          :read-only="detailsEditModeReadOnly"
                          @onFormChange="$emit('onFormChange',$event)"
                          @savingLoad="updateSavingState($event)"
                          @updateLoad="$emit('updateLoad', $event)"
                          @cancelLoad="$emit('cancelLoad', $event)"
                          banner-title="The Planning Tool™"
                          :planning-load="planningLoad"
                          :loading="loading"
                          :company="company">

      <template slot="toolbarButtons">
        <v-btn v-if="detailsEditModeReadOnly"
               flat color="accent"
               @click="toggleDetailsEditMode">
          <v-layout column>
            <v-icon>edit</v-icon>
            Edit
          </v-layout>
        </v-btn>
        <template v-if="detailsEditModeReadOnly">
          <v-btn flat color='accent'
                 :disabled="loading"
                 @click="assignTrucksDialog">
            <v-layout column>
              <v-icon>headset_mic</v-icon>
              Direct Dispatch
            </v-layout>
          </v-btn>
          <v-btn v-if="isQuickbooksConnected(company)"
                 flat color='accent'
                 :disabled="loading || !shouldUseQuickbooks(company)"
                 @click="assignVendorDialog">
            <v-layout column>
              <img height="24" width="24"
                   class="mx-auto"
                   src="/static/Quickbooks-Logo.png"/>
              Vendor Dispatch
            </v-layout>
          </v-btn>
          <v-btn v-else flat color='accent'
                 :disabled="loading"
                 @click="noteOutsideAssignmentDialog">
            <v-layout column>
              <v-icon>phonelink_ring</v-icon>
              Phone Dispatch
            </v-layout>
          </v-btn>
        </template>
        <template v-else>
          <v-btn flat color="accent"
                 :disabled="savingLoad"
                 @click="toggleDetailsEditMode">
            <v-layout column>
              <v-icon>clear</v-icon>
              Ignore
            </v-layout>
          </v-btn>
          <v-btn color="accent"
                 :loading="savingLoad"
                 @click="saveDetailsUpdates">
            <v-layout column>
              <v-icon>save</v-icon>
              Save
            </v-layout>
          </v-btn>
        </template>
        <v-btn v-if="detailsEditModeReadOnly"
               flat color="accent" @click="openCopyLoadDialog">
          <v-layout column>
            <v-icon>redo</v-icon>
            Repeat
          </v-layout>
        </v-btn>
      </template>

      <template slot="dispatchTab">
        <v-toolbar :extended="isMobile" :dense="!isMobile" flat>
          <v-toolbar-title v-if="isMobile">
            <v-layout>
              <v-flex align-self-center>
                {{ visibleDispatchRequests.length }} dispatch {{ visibleDispatchRequests.length | pluralize('request') }}
              </v-flex>
              <v-flex align-self-center class="text-sm-right text-xs-right">
                <v-btn-toggle multiple>
                  <v-btn v-model="showCanceledNetworkRequests">
                    <v-icon color="accent"
                            v-if="showCanceledNetworkRequests">
                      highlight_off
                    </v-icon>
                    <v-icon v-else>highlight_off</v-icon>
                  </v-btn>
                </v-btn-toggle>
                <v-btn flat fab small
                       color="primary">
                  <v-icon @click="showCarrierNetworkInfoDialog = true">
                    help_outline
                  </v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar-title>
          <v-toolbar-title v-else>
            {{ visibleDispatchRequests.length }} dispatch {{ visibleDispatchRequests.length | pluralize('request') }}
          </v-toolbar-title>
          <template v-if="isMobile" slot="extension">
            <v-layout column>
              <v-flex>
                <v-btn color="accent"
                       flat
                       @click="dispatchToNetworkDialog">
                  <v-icon class="pr-2">share</v-icon>
                  Send Network Request
                </v-btn>
              </v-flex>
            </v-layout>
          </template>
          <template v-else>
            <v-spacer/>
            <v-btn color="accent"
                   flat
                   @click="dispatchToNetworkDialog">
              <v-layout column>
                <v-icon class="pr-2">share</v-icon>
                Send Network Request
              </v-layout>
            </v-btn>
            <v-btn-toggle multiple>
              <v-btn v-model="showCanceledNetworkRequests">
                <v-icon color="accent"
                        v-if="showCanceledNetworkRequests">
                  highlight_off
                </v-icon>
                <v-icon v-else>highlight_off</v-icon>
              </v-btn>
              <v-btn flat fab small
                     color="primary">
                <v-icon @click="showCarrierNetworkInfoDialog = true">
                  help_outline
                </v-icon>
              </v-btn>
            </v-btn-toggle>
          </template>
        </v-toolbar>
        <v-container fluid>
          <v-layout :column="isMobile"
                    :row="!isMobile" :wrap="!isMobile" align-start justify-start>
            <v-flex xs4 v-if="planningLoad && (visibleDispatchRequests.length > 0)"
                    :key="`dispatch_${dispatch.humanId}`"
                    v-for="dispatch in visibleDispatchRequests">
              <v-dispatch-request :key="dispatch.humanId"
                                  :dispatch="dispatch"
                                  :load="planningLoad"
                                  @refreshLoad="refreshLoad()">
              </v-dispatch-request>
            </v-flex>
          </v-layout>
        </v-container>
      </template>

      <template slot="liveLoadBoardTab" v-if="isLoadboardEnabled">
        <v-toolbar :extended="isMobile" :dense="!isMobile" flat>
          <v-toolbar-title>
            Live Load Board Activity
          </v-toolbar-title>
          <template v-if="isMobile" slot="extension">
            <v-text-field v-if="isPostedToLiveLoadBoard(planningLoad)"
                          v-model="bidSearch"
                          append-icon="search"
                          class="single-line hide-details mb-0 pr-2"
                          label="Search bids">
            </v-text-field>
            <v-btn color="accent"
                   flat
                   v-else
                   @click="postToLoadBoardDialog(planningLoad)">
              <v-layout column>
                <v-icon class="pr-2">gavel</v-icon>
                Post to Live Load Board
              </v-layout>
            </v-btn>
            <v-btn-toggle multiple v-if="isPostedToLiveLoadBoard(planningLoad)">
              <v-btn v-model="showCanceledLoadBoardBids">
                <v-icon color="accent"
                        v-if="showCanceledLoadBoardBids">
                  highlight_off</v-icon>
                <v-icon v-else>highlight_off</v-icon>
              </v-btn>
              <v-btn v-model="showAcceptedLoadBoardBids">
                <v-icon color="accent"
                        v-if="showAcceptedLoadBoardBids">
                  done</v-icon>
                <v-icon v-else>done</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-btn flat fab small
                   color="primary">
              <v-icon @click="showLiveLoadBoardInfoDialog = true">
                help_outline
              </v-icon>
            </v-btn>
          </template>
          <template v-else>
            <v-spacer/>
            <v-text-field v-if="isPostedToLiveLoadBoard(planningLoad)"
                          v-model="bidSearch"
                          append-icon="search"
                          class="single-line hide-details mb-0 pr-2"
                          label="Search bids">
            </v-text-field>
            <v-btn color="accent"
                   flat
                   v-else
                   @click="postToLoadBoardDialog(planningLoad)">
              <v-layout column>
                <v-icon class="pr-2">gavel</v-icon>
                Post to Live Load Board
              </v-layout>
            </v-btn>
            <v-btn-toggle multiple v-if="isPostedToLiveLoadBoard(planningLoad)">
              <v-btn v-model="showCanceledLoadBoardBids">
                <v-icon color="accent"
                        v-if="showCanceledLoadBoardBids">
                  highlight_off</v-icon>
                <v-icon v-else>highlight_off</v-icon>
              </v-btn>
              <v-btn v-model="showAcceptedLoadBoardBids">
                <v-icon color="accent"
                        v-if="showAcceptedLoadBoardBids">
                  done</v-icon>
                <v-icon v-else>done</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-btn flat fab small
                   color="primary">
              <v-icon @click="showLiveLoadBoardInfoDialog = true">
                help_outline
              </v-icon>
            </v-btn>
          </template>

        </v-toolbar>

        <v-layout row wrap mt-2
                  v-if="isLoadboardEnabled && isPostedToLiveLoadBoard(planningLoad)">
          <v-card class="ml-2 mr-3 mb-3">
            <v-container class="pa-0">
              <v-toolbar class="ml-0" card dense>
                <v-icon color="primary">gavel</v-icon>
                <v-toolbar-title>
                  Accepting Bids
                </v-toolbar-title>
                <v-spacer/>
                <v-icon color="accent"
                        @click="removeFromLoadBoard(planningLoad)">
                  cancel
                </v-icon>
              </v-toolbar>

              <v-list>
                <v-list-tile v-if="planningLoad.customerCompany">
                  <v-list-tile-action>
                    <v-chip color="primary" text-color="white" small>
                      <v-icon v-if="planningLoad.loadBoard.includeCustomer">person_add</v-icon>
                      <v-icon v-else>person_add_disabled</v-icon>
                    </V-chip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>Customer <span v-if="!planningLoad.loadBoard.includeCustomer">not</span> included</v-list-tile-title>
                    <v-list-tile-sub-title>on the Load Details and {{ $t('terms.bill-of-lading') }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile v-if="planningLoad.customerCompany">
                  <v-list-tile-action>
                    <v-chip color="primary" text-color="white" small>
                      <v-icon>monetization_on</v-icon>
                    </v-chip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title v-if="planningLoad.billableAmount != null">
                      Pays ${{ planningLoad.billableAmount }}
                    </v-list-tile-title>
                    <v-list-tile-title v-else>
                      <span class="font-weight-bold font-italic">Missing billing amount</span>
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile v-if="!planningLoad.customerCompany">
                  <v-list-tile-title>
                    This is your load
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile >
                  <v-list-tile-action>
                    <v-chip color="primary" text-color="white" small>
                      <v-icon>list</v-icon>
                    </v-chip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      Posted to Live Load Board
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ planningLoad.loadBoard.published | formatDate}}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile>
                  <v-list-tile-action>
                    <v-chip small
                            color="primary">
                      <v-icon color="white">
                        add_shopping_cart
                      </v-icon>
                    </v-chip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ allBids.length }} bids</v-list-tile-title>
                    <v-list-tile-sub-title>{{ activeBids.length }} active</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-list-tile>
                  <v-list-tile-action>
                    <v-chip color="primary" text-color="white" small>
                      <v-icon>visibility</v-icon>
                    </v-chip>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      {{ planningLoad.loadBoard.visibleCount }} visible
                      {{ planningLoad.loadBoard.visibleCount | pluralize('load', { includeNumber: false }) }}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-container>
          </v-card>

          <v-bid-card v-for="bid in bids"
                      :key="`bid_card_${bid.humanId}`"
                      :bid="bid"
                      :load="planningLoad"
                      @refresh="onRefresh()">

          </v-bid-card>

        </v-layout>
        <v-layout row wrap mt-2 v-else>
          <v-card class="ma-3">
            <v-container class="pa-0">
              <v-toolbar class="ml-0" card dense>
                <v-icon color="primary">gavel</v-icon>
                <v-toolbar-title>
                  Post to Live Board
                </v-toolbar-title>
                <v-spacer/>
                <v-icon color="accent" @click="postToLoadBoardDialog(planningLoad)">
                  add_circle
                </v-icon>
              </v-toolbar>

              <v-list>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      Find an Outside Truck
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      Get bids from {{ $t('site-title.brief') }}'s available, qualified carriers
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-container>
          </v-card>
        </v-layout>
      </template>

      <template slot="loadMap">
        <load-map class="py-1 px-1"
                  hideOnSmall
                  v-if="planningLoad"
                  :origin-place-id="_.get(planningLoad, 'loadGroup.details.pickupLocation.googlePlace.placeId', null)"
                  :destination-place-id="_.get(planningLoad, 'loadGroup.details.dropoffLocation.googlePlace.placeId', null)">
        </load-map>
      </template>
    </v-load-group-details>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import Vue2Filters from 'vue2-filters';

import { isTruckingCompany } from '@/types/models/company';

import API from '@/api';
import planningLoadGroupsMixin from '@/mixins/planningLoadGroupsMixin';
import companyNames from '@/mixins/companyNames';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import QuickbooksMixin from '@/mixins/quickbooksMixin';

import * as types from '@/store/mutationTypes';
import { bidStatus } from '@/types';

import VQuickbooksBtn from '@/components/qb/QuickbooksButton';
import VendorDispatchDialog from '@/components/modals/VendorDispatchDialog';
import LoadMap from '@/components/LoadMap';
import VFloatField from '@/components/VFloatField';
import VBidCard from '@/pages/Companies/Loads/components/BidCard';
import VDispatchRequest from '@/pages/Companies/Loads/components/DispatchRequest';
import VRepeatLoadGroupDialog from '@/components/RepeatLoadGroupDialog';
import VLoadGroupDetails from './LoadGroupDetails';
import AssignTruckDialog from '../../modals/AssignTruckDialog';
import pluginMixin from '@/mixins/pluginMixin';
import unitsOfMeasurePlugin from '@/mixins/unitsOfMeasurePlugin';

export default {
  name: 'VPlannedLoadGroupDetails',
  components: {
    AssignTruckDialog,
    VRepeatLoadGroupDialog,
    LoadMap,
    VBidCard,
    VFloatField,
    VLoadGroupDetails,
    VDispatchRequest,
    VQuickbooksBtn,
    VendorDispatchDialog,
  },
  mixins: [
    companyNames,
    planningLoadGroupsMixin,
    bookedLoadMixin,
    Vue2Filters.mixin,
    QuickbooksMixin,
    pluginMixin,
    unitsOfMeasurePlugin,
  ],
  props: {
    company: {
      type: Object,
      required: true,
    },
    planningLoad: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    planningLoad(newVal, oldVal) {
      if (newVal.humanId !== oldVal.humanId) {
        this.detailsEditModeReadOnly = true;
      }
    },
    bookLoadAssignments(newVal, oldval) {
      if (newVal) {
        this.selectedMembers = null;
      } else {
        this.selectedMembers = [];
      }

    },
  },
  data: () => ({
    visibleCount: null,
    showLiveLoadBoardInfoDialog: false,
    showCarrierNetworkInfoDialog: false,
    showAssignTrucksDialog: false,
    showAssignVendorDialog: false,
    showOutsideAssignmentDialog: false,
    showCanceledNetworkRequests: false,
    showCopyLoadDialog: false,

    showDispatchToNetworkDialog: false,
    dispatchPayableAmount: null,
    includeCustomerOnDispatch: true,
    cdnMembers: [],
    selectedMembers: [],
    bookLoadAssignments: false,

    showPostToLoadBoardDialog: false,

    showAcceptedLoadBoardBids: false,
    showCanceledLoadBoardBids: false,
    bidSearch: null,

    vendorPayableAmount: null,

    loadingBtn: false,
    savingLoad: false,

    // edit details
    detailsEditModeReadOnly: true,
    customOrigin: null,
    customDestination: null,
  }),
  computed: {
    _() {
      return _;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    includeCustomerIcon() {
      return this.includeCustomerOnDispatch ? 'person_add' : 'person_add_disabled';
    },
    includeCustomerOnDispatchLabel() {
      return `Include customer on the load and ${this.$t('terms.bill-of-lading')}`;
    },
    loadGroup() {
      return _.get(this.planningLoad, 'loadGroup', {});
    },

    loadedMiles() {
      return _.get(this.planningLoad, 'loadGroup.details.loadedMiles', 'not this time');
    },
    loadedDistanceBilledRate() {
      return this.distanceRate(this.billableAmount, this.loadedMiles);
    },
    loadedDistancePaidRate() {
      if (this.dispatchPayableAmount) {
        return this.distanceRate(this.dispatchPayableAmount, this.loadedMiles);
      }

      return '';
    },
    originPlaceId() {
      return this.customOrigin || _.get(this.planningLoad, 'pickupLocation.googlePlace.placeId', null);
    },
    destinationPlaceId() {
      return this.customDestination || _.get(this.planningLoad, 'dropoffLocation.googlePlace.placeId', null);
    },

    billableAmount() {
      return _.get(this.planningLoad, 'loadGroup.amount');
    },
    acceptBookLoadAssignments() {
      return this.bookLoadAssignments;
    },
    allCdnSelected() {
      return this.selectedMembers.length === this.cdnMembers.length;
    },
    icon() {
      if (this.allCdnSelected) return 'check_box';
      if (this.selectedMembers.length > 0 && !this.allCdnSelected) return 'indeterminate_check_box';
      return 'check_box_outline_blank';
    },
    // todo: use load group mixin: loadGroupCustomer().name. this might not exist
    planningLoadCustomerName() {
      return _.get(this.planningLoad, 'loadGroup.details.customerCompany.name');
    },
    visibleDispatchRequests() {
      const requests = _.get(this.planningLoad, 'dispatched', []);
      const visible = _.filter(requests,
        dr => this.showCanceledNetworkRequests || !dr.retracted);
      return _.orderBy(visible, ['status', 'created'], ['asc', 'desc']);
    },
    allBids() {
      return _.get(this.planningLoad, 'bidDetails.bids', []);
    },
    activeBids() {
      return this.allBids
        .filter(bid => this.isBidActive(bid));
    },
    bids() {
      return this.allBids
        .filter(bid => this.filterBid(bid))
        .filter((bid) => {
          const isActive = this.isBidActive(bid);
          const isAccepted = this.isBidAccepted(bid);
          const isCanceled = this.isBidCanceled(bid);

          let visible = isActive;

          visible = visible || (isCanceled && this.showCanceledLoadBoardBids);
          visible = visible || (isAccepted && this.showAcceptedLoadBoardBids);

          return visible;
        })
        .map(b => ({ ...b,
          numberOfLoadsSelections: b.numberOfLoads,
          listing: this.listing })) || {};
    },
  },
  methods: {
    async removeFromLoadBoard(load) {
      try {
        this.loadingBtn = true;

        const apiResponse = await API.planner.removeFromLoadBoard({
          loadId: load.humanId,
        });

        this.$emit('updateLoad', apiResponse.data);
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingBtn = false;
      }
    },
    updateSavingState(newState) {
      if (this.savingLoad && this.savingLoad !== newState) {
        this.detailsEditModeReadOnly = true;
      }

      this.savingLoad = newState;
    },
    isDispatchActive(dispatch) {
      return !(dispatch.status === 'CANCELED');
    },
    resetDispatchToNetworkDialog() {
      this.showDispatchToNetworkDialog = false;
      this.bookLoadAssignments = false;
    },
    selectAllCDNMembers() {
      this.$nextTick(() => {
        if (this.allCdnSelected) {
          this.selectedMembers = [];
        } else {
          this.selectedMembers = this.cdnMembers.slice();
        }
      });
    },
    async dispatchToNetworkDialog() {
      try {
        this.loadingBtn = true;
        this.visibleCount = _.get(this.planningLoad, 'loadGroup.loadCount');
        this.showDispatchToNetworkDialog = true;
        this.bookLoadAssignments = false;

        const apiResponse = await API.dispatchers.getCompanyCDNs({
          companyId: this.loadGroup.details.listingCompany.id,
        });

        this.cdnMembers = _.filter(apiResponse.data.members, isTruckingCompany); // TC members
        this.selectedMembers = this.cdnMembers.slice();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingBtn = false;
      }
    },
    async dispatchLoad() {
      let bookLoadAssignments = this.acceptBookLoadAssignments;
      let selectedMembers = this.selectedMembers;
      if (this.allCdnSelected) {
        selectedMembers = null;
        bookLoadAssignments = false;
      }
      // the select is multiple unless bookLoadAssignments is checked
      if (bookLoadAssignments) {
        selectedMembers = [this.selectedMembers];
      }

      const body = {
        includeCustomerOnLoad: this.planningLoadCustomerName ? this.includeCustomerOnDispatch : false,
        payableAmount: this.dispatchPayableAmount,
        selectedMemberCompanyIds: selectedMembers,
        visibleCount: this.visibleCount,
        bookLoadAssignments: bookLoadAssignments,
      };
console.log('going to dispatch to network pals', this.planningLoad.humanId, body);
      try {
        this.$store.commit(`applications/${types.START_LOADING}`);

        await API.planner.dispatch({ loadId: this.planningLoad.humanId, body });

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Sent Dispatch Request',
          timeout: 4000,
        });

        this.resetDispatchToNetworkDialog();
        this.reloadCompanyPlanningLoadGroups(this.company.id);
        this.refreshLoad();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.selectedMembers = [];
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    async refreshLoad() {
      try {
        this.loadingLoad = true;
        this.$store.commit(`applications/${types.START_LOADING}`);
        const apiResponse = await API.planner.getLoad(this.planningLoad.humanId);
        this.$emit('updateLoad', apiResponse.data);
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingLoad = false;
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },

    postToLoadBoardDialog(planningLoad) {
      this.visibleCount = _.get(planningLoad, 'loadGroup.loadCount');
      this.showPostToLoadBoardDialog = true;
    },
    resetPostToLoadBoardDialog() {
      this.showPostToLoadBoardDialog = false;
    },
    async postToLoadBoard(includeCustomer) {
      if (!this.isLoadboardEnabled){
        this.$store.dispatch('applications/setErrorNotification', {
          message: "Posting loads to the Loadboard is not supported",
        });

        return;
      }

      const body = {
        includeCustomerOnLoad: includeCustomer,
        visibleCount: this.visibleCount,
      };

      try {
        this.$store.commit(`applications/${types.START_LOADING}`);

        await API.planner.postToLoadBoard({ loadId: this.planningLoad.humanId, body });

        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Posted for Bids',
          timeout: 4000,
        });

        this.resetPostToLoadBoardDialog();
        this.reloadCompanyPlanningLoadGroups(this.company.id);
        this.refreshLoad();
      } catch (error) {
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    isPostedToLiveLoadBoard(load) {
      return _.get(load, 'loadBoard.published');
    },
    filterBid(bid) {
      return !this.bidSearch ||
        this.filter(bid.humanId, this.bidSearch) ||
        this.filter(bid.truckingCompany.name, this.bidSearch);
    },
    isBidActive(bid) {
      return bidStatus.ACTIVE === bid.status;
    },
    isBidAccepted(bid) {
      return bidStatus.ACCEPTED === bid.status;
    },
    isBidCanceled(bid) {
      const isActive = this.isBidActive(bid);
      const isAccepted = this.isBidAccepted(bid);

      return !isAccepted && !isActive;
    },
    assignTrucksDialog() {
      this.showAssignTrucksDialog = true;
    },
    updateLoad(event) {
      this.$emit('updateLoad', event.data);
      this.loadBookedLoads(this.company.id);
    },
    assignVendorDialog() {
      this.resetAssignVendorDialog();
      this.showAssignVendorDialog = true;
    },
    // said another way, this is our phone dispatch
    noteOutsideAssignmentDialog() {
      this.resetNoteOutsideAssignmentDialog();
      this.showOutsideAssignmentDialog = true;
    },
    resetAssignVendorDialog() {
      this.showAssignVendorDialog = false;
      this.assignTruckCount = false;

      this.includeCustomerOnDispatch = true;
    },
    resetNoteOutsideAssignmentDialog() {
      this.showOutsideAssignmentDialog = false;
      this.assignTruckCount = false;

      this.includeCustomerOnDispatch = true;
    },
    toggleDetailsEditMode() {
      // TODO: permissions
      if (this.detailsEditModeReadOnly) {
        // how to reset the modify state?
      } else {
        // how to set up the modify state?
      }

      this.detailsEditModeReadOnly = !this.detailsEditModeReadOnly;
    },
    saveDetailsUpdates() {
      this.$bus.$emit('saveEditLoad');
    },
    openCopyLoadDialog() {
      this.showCopyLoadDialog = true;
    },
    closeCopyLoadDialog(success) {
      this.showCopyLoadDialog = false;

      if (success) {
        this.$store.dispatch('applications/setSuccessNotification', {
          message: 'Load Group Created in the Planner',
          timeout: 3000,
        });
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/dashboard/simple/PlannedLoadGroupDetails.vue